'use strict';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { Alert, Box, CircularProgress, CssBaseline, Snackbar, Typography } from '@mui/material';
import api from './api/api';
import LoginForm from './pages/components/LoginForm';
import POS from './pages/POS';
import { clearAuthSession, readAuthSession, commitAuthSession } from './auth/authStorage';

const POS_PERMISSION = 'pos:terminal:ver';

function normalizePermission(value) {
  if (typeof value === 'string') return value.trim().toLowerCase();
  return String(value?.name || value?.permission || value?.code || value?.clave || '').trim().toLowerCase();
}
function getTenantId(t) { return t?.tenantid || t?.tenantId || t?.tenant_id || t?.id_tenant || t?.id || ''; }
function permissionsOf(t) { return [...new Set((Array.isArray(t?.permissions) ? t.permissions : Array.isArray(t?.permisos) ? t.permisos : []).map(normalizePermission).filter(Boolean))]; }
function findTenant(payload, tenantId) {
  const list = Array.isArray(payload?.allowedTenants) ? payload.allowedTenants : Array.isArray(payload?.allowed_tenants) ? payload.allowed_tenants : [];
  return list.find((t) => String(getTenantId(t)) === String(tenantId)) || null;
}
function Loader() { return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>; }

function RequirePOS({ authenticated, allowed, children }) {
  const location = useLocation();
  if (!authenticated) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (!allowed) return <Navigate to="/sin-acceso" replace />;
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [snack, setSnack] = useState({ open: false, severity: 'info', message: '' });
  const allowed = useMemo(() => permissions.includes(POS_PERMISSION), [permissions]);

  const notify = useCallback((message, severity='info') => setSnack({ open: true, message, severity }), []);

  const verify = useCallback(async () => {
    setLoading(true);
    const session = readAuthSession();
    if (!session.token || !session.tenantid) {
      setAuthenticated(false); setPermissions([]); setLoading(false); return;
    }
    try {
      const decoded = jwtDecode(session.token);
      if (decoded?.exp && decoded.exp < Date.now()/1000) throw new Error('TOKEN_EXPIRED');
      const { data } = await api.get('/auth/me', { timeout: 6000 });
      const tenant = findTenant(data, session.tenantid);
      if (!tenant) throw new Error('TENANT_ACCESS_NOT_FOUND');
      const perms = permissionsOf(tenant);
      commitAuthSession({ token: session.token, user: data?.user || session.user, tenant: { ...(session.tenant || {}), ...tenant, permissions: perms } });
      setPermissions(perms); setAuthenticated(true);
    } catch (error) {
      const cached = permissionsOf(session.tenant || {});
      const recoverable = !error?.response || [408,425,429,500,502,503,504,530].includes(Number(error?.response?.status || 0));
      if (recoverable && cached.length) {
        setPermissions(cached); setAuthenticated(true);
        notify('API no disponible: sesión POS iniciada con permisos previamente validados.', 'warning');
      } else {
        clearAuthSession(); setAuthenticated(false); setPermissions([]);
      }
    } finally { setLoading(false); }
  }, [notify]);

  useEffect(() => { verify(); }, [verify]);

  useEffect(() => {
    const id = api.interceptors.response.use((r) => r, (error) => {
      if (error?.response?.status === 401) {
        clearAuthSession(); setAuthenticated(false); setPermissions([]);
        navigate('/login', { replace: true });
      }
      return Promise.reject(error);
    });
    return () => api.interceptors.response.eject(id);
  }, [navigate]);

  const handleLogin = useCallback(async ({ token, user, tenant }) => {
    setLoading(true);
    try {
      commitAuthSession({ token, user, tenant });
      const { data } = await api.get('/auth/me', { timeout: 6000 });
      const current = findTenant(data, getTenantId(tenant));
      if (!current) throw new Error('TENANT_ACCESS_NOT_FOUND');
      const perms = permissionsOf(current);
      commitAuthSession({ token, user: data?.user || user, tenant: { ...tenant, ...current, permissions: perms } });
      setPermissions(perms); setAuthenticated(true);
      navigate('/', { replace: true });
      notify('Sesión POS iniciada.', 'success');
    } catch (error) {
      clearAuthSession(); setAuthenticated(false); setPermissions([]);
      notify('No fue posible validar la empresa y permisos del POS.', 'error');
      navigate('/login', { replace: true });
    } finally { setLoading(false); }
  }, [navigate, notify]);

  if (loading) return <Loader />;

  return <>
    <Routes>
      <Route path="/login" element={authenticated ? <Navigate to="/" replace /> : <LoginForm onLogin={handleLogin} />} />
      <Route path="/sin-acceso" element={
        <Box sx={{ minHeight:'100vh', display:'grid', placeItems:'center', p:3 }}>
          <Box sx={{ textAlign:'center', maxWidth:560 }}>
            <Typography variant="h5" fontWeight={900}>Acceso POS no autorizado</Typography>
            <Typography color="text.secondary" sx={{ mt:1 }}>La empresa o perfil seleccionado no tiene el permiso <code>{POS_PERMISSION}</code>.</Typography>
          </Box>
        </Box>
      } />
      <Route path="/" element={<RequirePOS authenticated={authenticated} allowed={allowed}><POS /></RequirePOS>} />
      <Route path="/pos" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to={authenticated ? '/' : '/login'} replace />} />
    </Routes>
    <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s)=>({...s,open:false}))} anchorOrigin={{vertical:'bottom',horizontal:'center'}}>
      <Alert severity={snack.severity} onClose={() => setSnack((s)=>({...s,open:false}))}>{snack.message}</Alert>
    </Snackbar>
  </>;
}

export default function App() {
  return <BrowserRouter><CssBaseline /><AppRoutes /></BrowserRouter>;
}
